import React, { useState, useEffect, useCallback } from 'react';
import {
    Building2, Search, Plus, Pencil, X, Check, AlertTriangle,
    ChevronLeft, ChevronRight, RotateCcw
} from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/useStore';
import { useSporco, useUscitaProtetta } from '../utils/uscitaProtetta';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import '../styles/SoggettiFatturazione.css';
import { soloCampiPersona } from '../utils/campiPersona';

/**
 * SoggettiFatturazione — le aziende a cui si intestano le fatture.
 *
 * Non sono pazienti: non hanno sesso, data di nascita, cartella
 * clinica o consensi. Tenerle in una schermata a parte evita di
 * riempire di colonne vuote l'anagrafica dei pazienti.
 *
 * I soggetti "persona" nascono dalla scheda del paziente, alla sua prima
 * fattura, come COPIA dell'anagrafica (migration 243): se poi la residenza
 * cambia, l'intestatario non cambia da solo. Qui si corregge il loro
 * indirizzo - indirizzo, CAP, citta', provincia, nazione - (3/10/2026, prova
 * D3 di Alex: la Cassa mandava qui a completare il CAP, e la riga della
 * persona non aveva «Modifica») e, dal 4/10/2026, partita IVA, codice
 * destinatario e PEC, per il paziente che vuole la fattura con la sua
 * partita IVA (Alex, 3/10 sera, risposta 1 alle domande fiscali di D3: «si',
 * in "Modifica" della persona»). Nome, cognome e codice fiscale sono del
 * paziente.
 */

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

const VUOTO = {
    tipo: 'azienda', denominazione: '', codice_fiscale: '', partita_iva: '',
    indirizzo: '', cap: '', citta: '', provincia: '', nazione: 'IT',
    codice_univoco: '', pec: '', note: '',
};

const SoggettiFatturazione = () => {
    const [soggetti, setSoggetti] = useState([]);
    const [totale, setTotale] = useState(0);
    const [pagine, setPagine] = useState(1);
    const [pagina, setPagina] = useState(1);
    const [caricamento, setCaricamento] = useState(true);

    const [ricerca, setRicerca] = useState('');
    const [tipo, setTipo] = useState('azienda');
    const [statoAttivo, setStatoAttivo] = useState('true');
    /* «Da controllare» (Alex, 6/10/2026): le persone con la casella
       «L'intestatario è il paziente» proposta e mai confermata. */
    const [daControllare, setDaControllare] = useState(false);

    const [form, setForm] = useState(null);   // null = chiuso
    /* La scheda chiede conferma se si chiude con qualcosa di diverso da
       com'era: sfondo, X e «Annulla» passano tutti da chiudi(). L'istantanea
       si prende all'apertura (apri), il salvataggio chiude. */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco(form, versione);
    const chiedi = useUscitaProtetta(sporco);
    const apri = (f) => {
        setForm({ ...f, _cf_iniziale: f.codice_fiscale || '', _cf_corretto: '', _motivo: '', _correggi: false });
        setErrore(null); setVersione((v) => v + 1);
    };
    const amministrazione = useAuthStore((s) => s.user)?.ruolo === 'admin';
    const chiudi = () => { if (chiedi()) setForm(null); };
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    /* L'avviso della correzione del codice fiscale quando la scheda del
       paziente ha un codice diverso (Alex, 4/10/2026, risposta 6): la frase
       la manda il server, e resta finche' non la si chiude. */
    const [avvisoScheda, setAvvisoScheda] = useState(null);

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await api.get('/fatturazione/soggetti/elenco', {
                params: {
                    q: ricerca || undefined,
                    tipo: tipo || undefined,
                    attivo: statoAttivo || undefined,
                    da_controllare: tipo === 'persona' && daControllare ? 'true' : undefined,
                    pagina,
                },
            });
            const d = r.data.data;
            setSoggetti(d.soggetti || []);
            setTotale(d.totale);
            setPagine(d.pagine || 1);
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare i soggetti'));
        } finally {
            setCaricamento(false);
        }
    }, [ricerca, tipo, statoAttivo, daControllare, pagina]);

    useEffect(() => { carica(); }, [carica]);

    /* I paesi per la nazione del soggetto (migration 243): dal server,
       cosi' il modulo offre solo codici che il database accetta. */
    const [paesi, setPaesi] = useState([]);
    useEffect(() => {
        api.get('/fatturazione/paesi')
            .then((r) => setPaesi(r.data.data.paesi || []))
            .catch(() => setPaesi([]));
    }, []);

    /* La ricerca riparte dalla prima pagina: restare alla dodicesima
       con tre risultati confonde. */
    useEffect(() => { setPagina(1); }, [ricerca, tipo, statoAttivo, daControllare]);

    const salva = async () => {
        try {
            setErrore(null);
            setInCorso(true);
            if (form.id) {
                const corpo = form.tipo === 'persona' ? soloCampiPersona(form) : form;
                const r = await api.put(`/fatturazione/soggetti/${form.id}`, corpo);
                setAvviso(r.data.message);
            } else {
                const r = await api.post('/fatturazione/soggetti', form);
                setAvviso(r.data.message || 'Soggetto creato');
            }
            setForm(null);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        } finally {
            setInCorso(false);
        }
    };

    /* La correzione di un codice valido, solo l'amministrazione: col motivo.
       Dal 4/10/2026 (Alex, risposta 5) i documenti gia' trasmessi allo SdI o
       al Sistema TS non la fermano: restano col loro codice, e la risposta
       dice quanti sono. */
    const correggiCf = async () => {
        try {
            setErrore(null);
            setAvvisoScheda(null);
            setInCorso(true);
            const r = await api.post(`/fatturazione/soggetti/${form.id}/codice-fiscale`,
                { codice_fiscale: form._cf_corretto, motivo: form._motivo });
            setAvviso(r.data.message);
            setAvvisoScheda(r.data.data?.avviso_scheda || null);
            setForm(null);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella correzione del codice fiscale'));
        } finally {
            setInCorso(false);
        }
    };

    const cambiaStato = async (s) => {
        try {
            setErrore(null);
            const r = s.attivo
                ? await api.delete(`/fatturazione/soggetti/${s.id}`)
                : await api.post(`/fatturazione/soggetti/${s.id}/riattiva`);
            setAvviso(r.data.message);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel cambio di stato'));
        }
    };

    const nome = (s) => s.denominazione || `${s.cognome || ''} ${s.nome || ''}`.trim() || '—';
    /* La scheda di una persona: l'indirizzo, e partita IVA, codice
       destinatario e PEC (vedi CAMPI_PERSONA). */
    const persona = form?.tipo === 'persona';

    return (
        <div className="sog-wrap">
            <IntestazioneSezione
                titolo="Soggetti di fatturazione"
                icona={<Building2 size={22} aria-hidden="true" />}
                azione={
                    <button className="sog-btn sog-btn--principale"
                            onClick={() => apri({ ...VUOTO })}>
                        <Plus size={16} /> Nuova azienda
                    </button>
                }
            />

            {errore && !form && (
                <div className="sog-errore">
                    <AlertTriangle size={16} /> {errore}
                    <button onClick={() => setErrore(null)}><X size={14} /></button>
                </div>
            )}
            {avviso && (
                <div className="sog-avviso">
                    <Check size={16} /> {avviso}
                    <button onClick={() => setAvviso(null)}><X size={14} /></button>
                </div>
            )}
            {avvisoScheda && (
                <div className="sog-attenzione" role="alert">
                    <AlertTriangle size={16} /> {avvisoScheda}
                    <button onClick={() => setAvvisoScheda(null)}><X size={14} /></button>
                </div>
            )}

            <div className="sog-filtri" data-non-trattiene>
                <div className="sog-ricerca">
                    <Search size={16} />
                    <input value={ricerca} onChange={(e) => setRicerca(e.target.value)}
                           placeholder={tipo === 'persona'
                               ? 'Cognome, nome, codice fiscale, città…'
                               : 'Ragione sociale, P.IVA, città…'} />
                </div>
                <div className="sog-scelte">
                    <button className={tipo === 'azienda' ? 'attivo' : ''}
                            onClick={() => setTipo('azienda')}>Aziende</button>
                    <button className={tipo === 'persona' ? 'attivo' : ''}
                            onClick={() => setTipo('persona')}>Persone</button>
                    <button className={tipo === '' ? 'attivo' : ''}
                            onClick={() => setTipo('')}>Tutti</button>
                </div>
                <div className="sog-scelte">
                    <button className={statoAttivo === 'true' ? 'attivo' : ''}
                            onClick={() => setStatoAttivo('true')}>Attivi</button>
                    <button className={statoAttivo === 'false' ? 'attivo' : ''}
                            onClick={() => setStatoAttivo('false')}>Disattivati</button>
                </div>
                {tipo === 'persona' && (
                    <div className="sog-scelte">
                        <button className={daControllare ? 'attivo' : ''}
                                title="Le persone con la casella «L'intestatario è il paziente» proposta e non ancora confermata"
                                onClick={() => setDaControllare(!daControllare)}>Da controllare</button>
                    </div>
                )}
            </div>

            {caricamento ? (
                <div className="sog-stato">Caricamento…</div>
            ) : soggetti.length === 0 ? (
                <div className="sog-stato">Nessun soggetto corrisponde ai filtri.</div>
            ) : (
                <>
                    <div className="sog-conteggio">
                        {totale} soggett{totale === 1 ? 'o' : 'i'}
                        {pagine > 1 && ` — pagina ${pagina} di ${pagine}`}
                    </div>

                    <div className="rm-scorre">
                    <table className="sog-tabella">
                        <thead>
                            <tr>
                                <th>Denominazione</th>
                                <th>Partita IVA</th>
                                <th>Codice fiscale</th>
                                <th>Città</th>
                                <th>Fatt. elettronica</th>
                                <th className="num">Documenti</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {soggetti.map((s) => (
                                <tr key={s.id} className={s.attivo ? '' : 'sog-disattivo'}>
                                    <td>
                                        {nome(s)}
                                        {s.tipo === 'persona' && (
                                            <span className="sog-etichetta">
                                                persona{s.id_paziente ? ` · ${s.id_paziente}` : ''}
                                            </span>
                                        )}
                                    </td>
                                    <td className="mono">{s.partita_iva || '—'}</td>
                                    <td className="mono">{s.codice_fiscale || '—'}</td>
                                    <td>
                                        {s.citta || '—'}
                                        {s.provincia ? ` (${s.provincia})` : ''}
                                    </td>
                                    <td>
                                        {s.codice_univoco || s.pec ? (
                                            <span className="sog-si">
                                                {s.codice_univoco || 'via PEC'}
                                            </span>
                                        ) : (
                                            <span className="sog-manca">da completare</span>
                                        )}
                                    </td>
                                    <td className="num">{s.documenti}</td>
                                    <td className="num">
                                        <button className="sog-icona" title="Modifica"
                                                onClick={() => apri({ ...s })}>
                                            <Pencil size={15} />
                                        </button>
                                        <button className="sog-icona"
                                                title={s.attivo ? 'Disattiva' : 'Riattiva'}
                                                onClick={() => cambiaStato(s)}>
                                            {s.attivo ? <X size={15} /> : <RotateCcw size={15} />}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>

                    {pagine > 1 && (
                        <div className="sog-pagine">
                            <button disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
                                <ChevronLeft size={16} /> Precedente
                            </button>
                            <span>{pagina} / {pagine}</span>
                            <button disabled={pagina >= pagine} onClick={() => setPagina(pagina + 1)}>
                                Successiva <ChevronRight size={16} />
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* ---------- scheda ---------- */}
            {form && (
                <div className="sog-modale-sfondo" onClick={chiudi}>
                    <div className="sog-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="sog-modale-testata">
                            <span>{persona ? 'Modifica intestatario' : form.id ? 'Modifica soggetto' : 'Nuova azienda'}</span>
                            <button onClick={chiudi}><X size={18} /></button>
                        </div>
                        <div className="sog-modale-corpo">
                            {errore && (
                                <div className="sog-errore">
                                    <AlertTriangle size={16} /> {errore}
                                </div>
                            )}

                            {/* I DOCUMENTI EMESSI NON CAMBIANO (Alex, 3/10/2026
                                sera; migration 296): all'emissione portano i
                                dati dell'intestatario di allora, e una correzione
                                fatta qui vale per le bozze e i documenti nuovi.
                                Chi corregge lo sa prima di salvare, e sa che per
                                un documento emesso serve una nota di credito. */}
                            {form.id && Number(form.emessi_congelati) > 0 && (
                                <p className="sog-nota">
                                    {Number(form.emessi_congelati) === 1
                                        ? 'Un documento di questo intestatario è già emesso e non cambia: porta i dati di quando è stato emesso. Quello che salvi vale per le bozze e per i documenti nuovi; per correggere quel documento serve una nota di credito.'
                                        : `${form.emessi_congelati} documenti di questo intestatario sono già emessi e non cambiano: portano i dati di quando sono stati emessi. Quello che salvi vale per le bozze e per i documenti nuovi; per correggerne uno serve una nota di credito.`}
                                </p>
                            )}
                            {/* I DOCUMENTI EMESSI PRIMA DEL 4/10/2026 E NON
                                STAMPATI SEGUONO ANCORA LA CORREZIONE (revisione
                                del 3/10/2026): sono rimasti sull'intestatario
                                vivo, e la stampa e la fattura elettronica lo
                                leggono quando si generano. Sono di collaudo. */}
                            {form.id && Number(form.emessi_da_stampare) > 0 && (
                                <p className="sog-manca">
                                    {Number(form.emessi_da_stampare) === 1
                                        ? 'Un documento di questo intestatario è già emesso e non ancora stampato: alla stampa porterà i dati che salvi adesso, e così la sua fattura elettronica se si rigenera.'
                                        : `${form.emessi_da_stampare} documenti di questo intestatario sono già emessi e non ancora stampati: alla stampa porteranno i dati che salvi adesso, e così le loro fatture elettroniche se si rigenerano.`}
                                </p>
                            )}

                            {persona ? (
                                <>
                                    <p className="sog-nota">
                                        <b>{nome(form)}</b>
                                        {form._cf_iniziale ? <> · <span className="mono">{form._cf_iniziale}</span></> : ''}
                                        {form.id_paziente ? ` · paziente ${form.id_paziente}` : ''}
                                    </p>
                                    <label className="sog-casella">
                                        <input type="checkbox" checked={form.e_il_paziente === true}
                                               onChange={(e) => setForm({ ...form, e_il_paziente: e.target.checked, _casella_toccata: true })} />
                                        <span>L&apos;intestatario è il paziente</span>
                                        {form.e_il_paziente_proposta && !form._casella_toccata && (
                                            <>
                                                <span className="rm-stato rm-stato-attenzione">proposta, da controllare</span>
                                                {/* Il gesto che conferma la proposta lasciando il valore com'e'
                                                    (Alex, 6/10 sera): parte con «Salva». */}
                                                <button type="button" className="sog-btn"
                                                        onClick={(e) => { e.preventDefault(); setForm({ ...form, _casella_toccata: true }); }}>
                                                    Confermo
                                                </button>
                                            </>
                                        )}
                                        {form.e_il_paziente_proposta && form._casella_toccata && (
                                            <span className="rm-stato">confermata: si salva con «Salva»</span>
                                        )}
                                    </label>
                                    <p className="sog-nota">
                                        Togli la spunta se le fatture le paga un genitore o un&apos;altra
                                        persona registrata sulla scheda del paziente: quando
                                        l&apos;amministrazione corregge il codice fiscale, l&apos;avviso
                                        della scheda del paziente vale solo per il paziente stesso.
                                    </p>
                                    <p className="sog-nota">
                                        Nome, cognome e codice fiscale vengono dall&apos;anagrafica del
                                        paziente. Qui si correggono indirizzo, CAP, città, provincia e
                                        nazione dell&apos;intestatario delle fatture, e partita IVA,
                                        codice destinatario e PEC se il paziente vuole la fattura con la
                                        sua partita IVA: la scheda del paziente non li cambia.
                                    </p>
                                    {form.codice_fiscale_completabile ? (
                                        <>
                                            <label className="sog-campo">
                                                <span>Codice fiscale</span>
                                                <input className="mono" maxLength={17} value={form.codice_fiscale || ''}
                                                       onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.replace(/\s/g, '').toUpperCase() })} />
                                            </label>
                                            <p className="sog-nota">
                                                Il codice fiscale manca o non è valido per il documento della
                                                scheda del paziente: completalo qui. Vale per le bozze e per i
                                                documenti nuovi.
                                            </p>
                                        </>
                                    ) : amministrazione ? (
                                        form._correggi ? (
                                            <>
                                                <label className="sog-campo">
                                                    <span>Codice fiscale corretto</span>
                                                    <input className="mono" maxLength={17} value={form._cf_corretto}
                                                           onChange={(e) => setForm({ ...form, _cf_corretto: e.target.value.replace(/\s/g, '').toUpperCase() })} />
                                                </label>
                                                <label className="sog-campo">
                                                    <span>Motivo della correzione</span>
                                                    <textarea rows={2} value={form._motivo}
                                                              onChange={(e) => setForm({ ...form, _motivo: e.target.value })} />
                                                </label>
                                                {/* Alex, 4/10/2026: il documento emesso non
                                                    cambia mai; di quelli non ancora trasmessi
                                                    al Sistema TS cambia solo il codice che
                                                    partira' (migration 297); quelli gia'
                                                    trasmessi non fermano piu' la correzione
                                                    (risposta 5, migration 305). */}
                                                <p className="sog-nota">
                                                    Il motivo resta nel registro. Il codice nuovo vale per i
                                                    documenti nuovi. I documenti già emessi restano come sono:
                                                    a quelli non ancora trasmessi al Sistema TS cambia solo il
                                                    codice fiscale che partirà, e accanto al documento resta
                                                    la correzione, con la data, chi l&apos;ha fatta e il
                                                    motivo. Quelli già trasmessi allo SdI o al Sistema TS
                                                    restano col codice di prima: per correggerli serve una
                                                    nota di credito. La scheda del paziente non cambia.
                                                </p>
                                                <button type="button" className="sog-btn"
                                                        disabled={inCorso || !form._cf_corretto || form._motivo.trim().length < 10}
                                                        onClick={correggiCf}>
                                                    Salva la correzione
                                                </button>
                                            </>
                                        ) : (
                                            <button type="button" className="sog-btn"
                                                    onClick={() => setForm({ ...form, _correggi: true })}>
                                                Correggi il codice fiscale
                                            </button>
                                        )
                                    ) : (
                                        <p className="sog-nota">
                                            {'Il codice fiscale è valido: se è sbagliato lo corregge l\'amministrazione, con un motivo.'}
                                        </p>
                                    )}
                                    <label className="sog-campo">
                                        <span>Partita IVA</span>
                                        {/* Un carattere sopra la colonna (varchar(30)), di proposito come per il codice destinatario: con 30 una partita IVA di 31 incollata si salvava troncata (beta, 6/10/2026); cosi' la rifiuta il server, col suo messaggio. Lo stesso per codice fiscale, provincia e CAP. */}
                                        <input className="mono" maxLength={31} value={form.partita_iva || ''}
                                               onChange={(e) => setForm({ ...form, partita_iva: e.target.value.replace(/\s/g, '') })} />
                                    </label>
                                    <p className="sog-nota">
                                        Con la partita IVA i documenti nuovi di questo intestatario vanno
                                        allo SdI e non al Sistema TS, anche con visite e prestazioni.
                                        Scrivila solo se il paziente chiede la fattura con la partita IVA.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <label className="sog-campo">
                                        <span>Ragione sociale *</span>
                                        <input value={form.denominazione || ''}
                                               onChange={(e) => setForm({ ...form, denominazione: e.target.value })} />
                                    </label>

                                    <div className="sog-riga">
                                        <label className="sog-campo">
                                            <span>Partita IVA *</span>
                                            <input className="mono" maxLength={31} value={form.partita_iva || ''}
                                                   onChange={(e) => setForm({ ...form, partita_iva: e.target.value.replace(/\s/g, '') })} />
                                        </label>
                                        <label className="sog-campo">
                                            <span>Codice fiscale</span>
                                            <input className="mono" maxLength={17} value={form.codice_fiscale || ''}
                                                   onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} />
                                        </label>
                                    </div>
                                </>
                            )}

                            <label className="sog-campo">
                                <span>Indirizzo</span>
                                <input value={form.indirizzo || ''}
                                       onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} />
                            </label>

                            <div className="sog-riga">
                                <label className="sog-campo">
                                    <span>Città</span>
                                    <input value={form.citta || ''}
                                           onChange={(e) => setForm({ ...form, citta: e.target.value })} />
                                </label>
                                <label className="sog-campo sog-campo--stretto">
                                    <span>Provincia</span>
                                    <input maxLength={3} value={form.provincia || ''}
                                           onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase() })} />
                                </label>
                                <label className="sog-campo sog-campo--stretto">
                                    <span>CAP</span>
                                    <input maxLength={13} value={form.cap || ''}
                                           onChange={(e) => setForm({ ...form, cap: e.target.value })} />
                                </label>
                            </div>

                            <label className="sog-campo">
                                <span>Nazione *</span>
                                <select value={form.nazione || ''}
                                        onChange={(e) => setForm({ ...form, nazione: e.target.value })}>
                                    {!form.nazione && <option value="">— scegli —</option>}
                                    {paesi.map((p) => (
                                        <option key={p.codice} value={p.codice}>{p.nome} ({p.codice})</option>
                                    ))}
                                </select>
                            </label>

                            {/* Il codice destinatario e la PEC anche per una persona
                                (4/10/2026): servono a chi ha la partita IVA. */}
                            <div className="sog-sezione">Fattura elettronica</div>
                            <p className="sog-nota">
                                {persona
                                    ? 'Per chi ha la partita IVA: dove lo SdI recapita la fattura. Senza, la fattura elettronica resta nell\'area riservata del cliente sul sito dell\'Agenzia delle Entrate.'
                                    : 'Serve almeno uno dei due: senza, il documento non può essere recapitato allo SdI.'}
                            </p>
                            <div className="sog-riga">
                                <label className="sog-campo">
                                    <span>Codice destinatario</span>
                                    {/* Il limite del campo e' sopra quello della colonna, di proposito (6/10/2026, beta): con maxLength 7 un codice di 8 si tagliava in silenzio e, se le prime 7 erano lettere e cifre, si salvava sbagliato. Lo stesso per la partita IVA (colonna di 30 caratteri dalla migration 303, campo di 31): il valore intero arriva al server, e il motivo lo dice la regola della migration 303. */}
                                    <input className="mono" maxLength={20} value={form.codice_univoco || ''}
                                           onChange={(e) => setForm({ ...form, codice_univoco: e.target.value.toUpperCase() })} />
                                </label>
                                <label className="sog-campo">
                                    <span>PEC</span>
                                    <input type="email" value={form.pec || ''}
                                           onChange={(e) => setForm({ ...form, pec: e.target.value })} />
                                </label>
                            </div>

                            {!persona && (
                                <>
                                    <label className="sog-campo">
                                        <span>Note</span>
                                        <textarea rows={2} value={form.note || ''}
                                                  onChange={(e) => setForm({ ...form, note: e.target.value })} />
                                    </label>
                                </>
                            )}

                            <div className="sog-modale-azioni">
                                <button className="sog-btn" onClick={chiudi}>Annulla</button>
                                <button className="sog-btn sog-btn--principale" onClick={salva}
                                        disabled={inCorso || (persona
                                            ? !form.nazione
                                            : !form.denominazione || !form.partita_iva)}>
                                    {form.id ? 'Salva' : 'Crea'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SoggettiFatturazione;
